import React from 'react';
import styled from 'styled-components';
import { Controller } from 'react-hook-form';

import { categoriesTypes, categoriesMobileDisplays } from 'shared/constants/customization';
import { Button } from 'shared/components';

import { useDispensary } from 'src/hooks/use-dispensary';

import { getCategoryLabels } from '../customize.utils';
import { radiusOptions } from '../helpers';
import { MenuCustomizationLayout } from '../components/menu-customization-layout';
import { Preview } from '../components/preview';
import { OptionPicker } from '../components/option-picker';
import { Heading, Input, Inputs, SubText } from '../customize.styles';

import { useCategoriesForm } from './use-categories-form';
import { CategoriesFormProps } from './categories.types';
import { ReorderModal } from './reorder-modal';
import { SelectImageModal } from './select-image-modal';

export function CategoriesForm({ data }: CategoriesFormProps): JSX.Element {
  const sectionName = 'Categories';
  const {
    control,
    handlePublish,
    handleReset,
    isDirty,
    previewData,
    handleToggleReorderModal,
    isReorderModalOpen,
    handleOpenSelectImageModal,
    handleCloseSelectImageModal,
    isSelectImageModalOpen,
    categoryToEdit,
    handleModalSave,
  } = useCategoriesForm({
    data,
  });

  const {
    categoriesV2: { items },
  } = previewData;
  const dispensary = useDispensary();
  const categoryLabels = getCategoryLabels(dispensary.location?.state);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const radiusOptionsWithoutDescription = radiusOptions.map(({ description, ...option }) => option);

  return (
    <MenuCustomizationLayout
      headerText={sectionName}
      subHeaderText='Select how product category cards will appear.'
      isDirty={isDirty}
      handlePublish={() => void handlePublish()}
      handleReset={handleReset}
      previewComponent={<Preview.HomePage data={previewData} />}
    >
      <Inputs>
        <Input>
          <Controller
            name='type'
            control={control}
            render={({ onChange, value }) => (
              <OptionPicker label='Type' options={categoriesTypes} selectedOption={value} onChange={onChange} />
            )}
          />
        </Input>

        <Input>
          <Controller
            name='style'
            control={control}
            render={({ onChange, value }) => (
              <OptionPicker
                label='Style'
                options={radiusOptionsWithoutDescription}
                selectedOption={value}
                onChange={onChange}
              />
            )}
          />
        </Input>

        <Input>
          <Controller
            name='mobileDisplay'
            control={control}
            render={({ onChange, value }) => (
              <OptionPicker
                label='Display (Mobile only)'
                options={categoriesMobileDisplays}
                selectedOption={value}
                onChange={onChange}
              />
            )}
          />
        </Input>
      </Inputs>

      <ReorderInputsSection>
        <Heading>Order</Heading>
        <CustomizeOrderSubText>Customize the order of categories on your menu.</CustomizeOrderSubText>
        <Button onClick={handleToggleReorderModal}>Customize Order</Button>
      </ReorderInputsSection>

      <Inputs>
        <Heading>Category Image</Heading>
        <SubText>Customize the category image that will appear on your menu.</SubText>

        <CategoryImagesList>
          {items.map((item) => {
            // Use cardImage for outlet collections, fallback to regular image
            const displayImage = item.isOutlet && item.cardImage ? item.cardImage : item.image;
            // Use cardDisplayName for outlet collections, fallback to category label or category name
            const displayName =
              item.isOutlet && item.cardDisplayName
                ? item.cardDisplayName
                : categoryLabels?.[item.category] ?? item.category;

            return (
              <CategoryImageItem key={item.category}>
                <CategoryImageWrapper>
                  <img src={displayImage} alt='' />
                </CategoryImageWrapper>

                <CategoryImageName>{displayName}</CategoryImageName>

                <EditImageButton
                  onClick={() => handleOpenSelectImageModal(item)}
                  fontSize='12px'
                  height='32px'
                  width='116px'
                >
                  Edit Image
                </EditImageButton>
              </CategoryImageItem>
            );
          })}
        </CategoryImagesList>
      </Inputs>

      {isReorderModalOpen && (
        <ReorderModal categories={items} onSaveOrder={handleModalSave} onToggleModal={handleToggleReorderModal} />
      )}

      {categoryToEdit && isSelectImageModalOpen && (
        <SelectImageModal
          onClose={handleCloseSelectImageModal}
          category={categoryToEdit}
          categories={items}
          onSave={handleModalSave}
        />
      )}
    </MenuCustomizationLayout>
  );
}

const ReorderInputsSection = styled(Inputs)`
  margin-bottom: 48px;
`;

const CustomizeOrderSubText = styled(SubText)`
  padding-bottom: 16px;
`;

const CategoryImagesList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: space-between;
  list-style: none;
  max-width: 356px;
`;

const EditImageButton = styled(Button)`
  left: 50%;
  opacity: 0;
  position: absolute;
  top: 50%;
  transform: translate3d(-50%, -50%, 0);
  transition: opacity 0.2s;
  visibility: hidden;
`;

const CategoryImageItem = styled.li`
  align-items: center;
  border: 1px solid #e3e7e9;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
  overflow: hidden;
  padding: 16px;
  position: relative;
  width: calc(50% - 8px);

  &::before {
    background: #000000cc;
    content: '';
    height: 100%;
    left: 0;
    opacity: 0;
    position: absolute;
    top: 0;
    transition: opacity 0.2s;
    visibility: hidden;
    width: 100%;
  }

  &:hover {
    &::before,
    ${EditImageButton} {
      opacity: 1;
      visibility: visible;
    }
  }
`;

const CategoryImageWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;

  img {
    display: block;
    height: 45px;
  }
`;

const CategoryImageName = styled.div`
  color: #121516;
  font-size: 16px;
  font-weight: 600;
  line-height: ${24 / 16};
  text-align: center;
`;
